import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Icon,
  Flex,
} from '@chakra-ui/react';
import OrgTree from '../constant/OrgTree';
import TeamsList from '../constant/TeamList';
import httpInjectorService from 'services/http-injector.service';
import { demoManagersList } from 'components/demodata/Dummydata';
import { demoUsersReportingList } from 'components/demodata/Dummydata';

// ─── Constants ────────────────────────────────────────────────────────────────

const EXEC_TITLES = [
  'ceo',
  'cto',
  'coo',
  'director',
  'vp',
  'president',
  'chief',
];

const isExec = (designation) => {
  if (!designation) return false;

  return EXEC_TITLES.some((t) =>
    String(designation).toLowerCase().includes(t)
  );
};

// ─── Build Tree ───────────────────────────────────────────────────────────────

const buildTree = (users, managers) => {
  const managerByUserId = {};
  managers.forEach((m) => {
    managerByUserId[m.manager_user_id] = m;
  });

  const managerByRecordId = {};
  managers.forEach((m) => {
    managerByRecordId[m.reporting_manager_record_id] = m;
  });

  const userById = {};
  users.forEach((u) => {
    userById[u.id] = u;
  });

  const execUsers = users.filter((u) => isExec(u.designation));
  const execUserIds = new Set(execUsers.map((e) => e.id));

  const recordIdToExecUserId = {};
  managers.forEach((m) => {
    if (execUserIds.has(m.manager_user_id)) {
      recordIdToExecUserId[m.reporting_manager_record_id] = m.manager_user_id;
    }
  });

  const findExecForUser = (user, visited = new Set()) => {
    if (visited.has(user.id)) return null;
    visited.add(user.id);

    for (const auth of user.reportingAuthorities) {
      if (!auth.id) continue;
      if (recordIdToExecUserId[auth.id]) {
        return userById[recordIdToExecUserId[auth.id]];
      }
      const mgrRecord = managerByRecordId[auth.id];
      if (mgrRecord) {
        const mgrUser = userById[mgrRecord.manager_user_id];
        if (mgrUser && !visited.has(mgrUser.id)) {
          const found = findExecForUser(mgrUser, visited);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const execMap = {};
  execUsers.forEach((e) => {
    execMap[e.id] = { exec: e, members: [] };
  });

  users.forEach((u) => {
    if (isExec(u.designation)) return;
    const exec = findExecForUser(u);
    if (!exec || !execMap[exec.id]) return;
    execMap[exec.id].members.push(u);
  });

  const assignedIds = new Set([
    ...execUsers.map((e) => e.id),
    ...Object.values(execMap).flatMap((g) => g.members.map((m) => m.id)),
  ]);
  const orphans = users.filter((u) => !assignedIds.has(u.id));

  return { execGroups: Object.values(execMap), orphans };
};

// ─── Nested reporting tree under each exec (same APIs: primary manager slot) ─

const buildNestedUnderExec = (exec, members, managersList) => {
  if (!members?.length) return [];

  const recordToManagerUserId = {};
  managersList.forEach((m) => {
    recordToManagerUserId[m.reporting_manager_record_id] = m.manager_user_id;
  });

  const memberIds = new Set(members.map((m) => m.id));

  const getPrimaryManagerUserId = (user) => {
    const primary =
      user.reportingAuthorities?.find((a) => a.slot === 1) ||
      user.reportingAuthorities?.[0];
    if (!primary?.id) return null;
    return recordToManagerUserId[primary.id] ?? null;
  };

  const childrenByParentId = {};

  members.forEach((u) => {
    let parentId = getPrimaryManagerUserId(u);
    if (parentId === u.id) parentId = null;

    const underExec =
      parentId == null ||
      parentId === exec.id ||
      !memberIds.has(parentId);

    if (underExec) {
      if (!childrenByParentId.__exec__) childrenByParentId.__exec__ = [];
      childrenByParentId.__exec__.push(u);
    } else {
      if (!childrenByParentId[parentId]) childrenByParentId[parentId] = [];
      childrenByParentId[parentId].push(u);
    }
  });

  const sortByName = (a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

  const buildNode = (user) => ({
    ...user,
    children: (childrenByParentId[user.id] || [])
      .sort(sortByName)
      .map(buildNode),
  });

  return (childrenByParentId.__exec__ || [])
    .sort(sortByName)
    .map(buildNode);
};

// ─── Tab Icons ────────────────────────────────────────────────────────────────

const HierarchyIcon = () => (
  <Icon viewBox="0 0 24 24" w="15px" h="15px">
    <path
      fill="currentColor"
      d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z"
    />
  </Icon>
);

const ListIcon = () => (
  <Icon viewBox="0 0 24 24" w="15px" h="15px">
    <path
      fill="currentColor"
      d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"
    />
  </Icon>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Teams = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [execGroups, setExecGroups] = useState([]);
  const [orphans, setOrphans] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const isDemoMode = localStorage.getItem("demoMode") === "true";

    let usersRes;
    let managersRes;

    if (isDemoMode) {
      usersRes = { data: demoUsersReportingList };
      managersRes = { data: demoManagersList };
    } else {
      [usersRes, managersRes] = await Promise.all([
        httpInjectorService.getUsersReportingList(),
        httpInjectorService.getManagersList(),
      ]);
    }

        const formattedUsers = (usersRes?.data || []).map((user) => ({
          id: user.user_id,
          name: user.username,
          role: user.role,
          department: user.department_name,
          designation: user.designation_name || '',
          reportingAuthorities: [
            user.reporting_manager1_name && {
              id: user.reporting_manager1_record_id,
              name: user.reporting_manager1_name,
              slot: 1,
            },
            user.reporting_manager2_name && {
              id: user.reporting_manager2_record_id,
              name: user.reporting_manager2_name,
              slot: 2,
            },
          ].filter(Boolean),
        }));

        const managersList = managersRes?.data || [];

        const { execGroups: rawGroups, orphans: unassigned } = buildTree(
          formattedUsers,
          managersList,
        );

        const groups = rawGroups.map((g) => ({
          ...g,
          nestedMembers: buildNestedUnderExec(g.exec, g.members, managersList),
        }));

        setUsers(formattedUsers);
        setExecGroups(groups);
        setOrphans(unassigned);
      } catch (error) {
        console.error('Failed to load team data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box px={2} py={2} maxW="100%">
      <Tabs variant="unstyled" defaultIndex={0}>
        <Flex justifyContent="space-between" align="flex-start">
          {/* Page title */}
          <Box>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb='0px'>
              Teams
            </Text>
            <Text fontSize="13px" color="gray.400" mt="3px">
              View and manage your organization's team structure.
            </Text>
          </Box>
          {/* Tabs */}
          <TabList
            bg="gray.50"
            borderRadius="10px"
            p="3px"
            w="fit-content"
            border="1px solid"
            borderColor="gray.100"
          >
            <Tab
              fontSize="13px"
              fontWeight="500"
              color="gray.400"
              px={4}
              py={2}
              borderRadius="8px"
              display="flex"
              alignItems="center"
              gap={2}
              _selected={{
                bg: 'white',
                color: 'gray.700',
                fontWeight: '600',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
              _hover={{ color: 'gray.600' }}
              transition="all 0.15s"
            >
              <HierarchyIcon />
              Teams
            </Tab>
            <Tab
              fontSize="13px"
              fontWeight="500"
              color="gray.400"
              px={4}
              py={2}
              borderRadius="8px"
              display="flex"
              alignItems="center"
              gap={2}
              _selected={{
                bg: 'white',
                color: 'gray.700',
                fontWeight: '600',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
              _hover={{ color: 'gray.600' }}
              transition="all 0.15s"
            >
              <ListIcon />
              List
            </Tab>
          </TabList>
        </Flex>

        <TabPanels>
          {/* Tab 1 — Org Tree */}
          <TabPanel p={0}>
            <OrgTree
              execGroups={execGroups}
              orphans={orphans}
              totalCount={users.length}
              loading={loading}
            />
          </TabPanel>

          {/* Tab 2 — Card List */}
          <TabPanel p={0}>
            <TeamsList
              execGroups={execGroups}
              orphans={orphans}
              users={users}
              loading={loading}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default Teams;
